/**
 * Database Seed Data
 * 
 * Comprehensive seed data for the booking management system.
 * This file provides realistic sample data for testing and development.
 * 
 * @version 1.0.0
 * @see DATABASE_SCHEMA_DESIGN.md for schema documentation
 */

import {
  BookingTypeRecord,
  BookingRecord,
  BookingHistoryRecord,
  BookingConflictRecord,
  BookingTemplateRecord,
  BookingCategory,
} from '@/types/database';
import { generateDefaultFieldConfigurations, generateDefaultFieldOrder } from '@/utils/field-configurations';

/** Field order for the Project Work booking type — matches the specified form layout */
const PROJECT_FIELD_ORDER: string[] = [
  'Booking Type',
  'Status',
  'Colour',
  'Project',
  'Cost Centre',
  'Customer',
  'Location',
  'Booking Reference',
  'Description',
  'Details',
  'From',
  'To',
];

/** Field configurations for the Project Work booking type — key project-related fields are visible */
function generateProjectFieldConfigurations() {
  const configs = generateDefaultFieldConfigurations();
  const visibleFields = new Set(['project', 'cost centre', 'customer', 'location', 'booking reference', 'description']);
  return configs.map(fc => ({
    ...fc,
    isVisible: visibleFields.has(fc.fieldName.toLowerCase()) ? true : fc.isVisible,
  }));
}

// ============================================================================
// BOOKING TYPES SEED DATA
// ============================================================================

/**
 * Seed data for booking types
 * Includes various categories with different configurations
 */
export const seedBookingTypes: BookingTypeRecord[] = [
  {
    id: 'bt-project-001',
    code: 'PROJECT',
    name: 'Project Work',
    description: 'Standard project work booking for client deliverables',
    category: 'project',
    active: true,
    isDefault: true,
    sortOrder: 1,
    autoUpdateTimesheet: true,
    doNotUpdateTimesheetWithHours: false,
    obeyUserWorkingProfileAndBankHolidays: true,
    autoAdjustBookingForWorkingProfileAndBankHolidays: false,
    addDaysToEndOfBooking: false,
    allowRepeatBookings: true,
    requiresApproval: false,
    allowOverlapping: false,
    fieldConfigurations: generateProjectFieldConfigurations(),
    fieldOrder: [...PROJECT_FIELD_ORDER],
    color: '#3b82f6',
    icon: 'Briefcase',
    allowedRoles: ['Administrator', 'User', 'Viewer'],
    createdAt: '2024-01-01T00:00:00.000Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00.000Z',
    updatedBy: 'system',
    version: 1,
    isTemporary: false,
  },
  {
    id: 'bt-absence-001',
    code: 'ABSENCE',
    name: 'Absence / Leave',
    description: 'Time off, vacation, sick leave, and other absences',
    category: 'absence',
    active: true,
    isDefault: false,
    sortOrder: 2,
    autoUpdateTimesheet: false,
    doNotUpdateTimesheetWithHours: false,
    obeyUserWorkingProfileAndBankHolidays: false,
    autoAdjustBookingForWorkingProfileAndBankHolidays: false,
    addDaysToEndOfBooking: true,
    allowRepeatBookings: false,
    requiresApproval: true,
    allowOverlapping: false,
    fieldConfigurations: generateDefaultFieldConfigurations(),
    fieldOrder: generateDefaultFieldOrder(),
    color: '#94a3b8',
    icon: 'Calendar',
    allowedRoles: ['Administrator', 'User'],
    createdAt: '2024-01-01T00:00:00.000Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00.000Z',
    updatedBy: 'system',
    version: 1,
    isTemporary: false,
  },
  {
    id: 'bt-training-001',
    code: 'TRAINING',
    name: 'Training & Development',
    description: 'Professional development, courses, and training sessions',
    category: 'training',
    active: true,
    isDefault: false,
    sortOrder: 3,
    autoUpdateTimesheet: true,
    doNotUpdateTimesheetWithHours: true,
    obeyUserWorkingProfileAndBankHolidays: true,
    autoAdjustBookingForWorkingProfileAndBankHolidays: true,
    addDaysToEndOfBooking: false,
    allowRepeatBookings: true,
    requiresApproval: true,
    allowOverlapping: false,
    fieldConfigurations: generateDefaultFieldConfigurations(),
    fieldOrder: generateDefaultFieldOrder(),
    color: '#10b981',
    icon: 'GraduationCap',
    allowedRoles: ['Administrator', 'User'],
    createdAt: '2024-01-01T00:00:00.000Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00.000Z',
    updatedBy: 'system',
    version: 1,
    isTemporary: false,
  },
  {
    id: 'bt-meeting-001',
    code: 'MEETING',
    name: 'Meeting',
    description: 'Internal and external meetings, calls, and discussions',
    category: 'meeting',
    active: true,
    isDefault: false,
    sortOrder: 4,
    autoUpdateTimesheet: true,
    doNotUpdateTimesheetWithHours: false,
    obeyUserWorkingProfileAndBankHolidays: false,
    autoAdjustBookingForWorkingProfileAndBankHolidays: false,
    addDaysToEndOfBooking: false,
    allowRepeatBookings: true,
    requiresApproval: false,
    allowOverlapping: true,
    fieldConfigurations: generateDefaultFieldConfigurations(),
    fieldOrder: generateDefaultFieldOrder(),
    color: '#f59e0b',
    icon: 'Users',
    allowedRoles: ['Administrator', 'User', 'Viewer'],
    createdAt: '2024-01-01T00:00:00.000Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00.000Z',
    updatedBy: 'system',
    version: 1,
    isTemporary: false,
  },
  {
    id: 'bt-internal-001',
    code: 'INTERNAL',
    name: 'Internal Work',
    description: 'Internal projects, admin work, and non-billable activities',
    category: 'internal',
    active: true,
    isDefault: false,
    sortOrder: 5,
    autoUpdateTimesheet: true,
    doNotUpdateTimesheetWithHours: false,
    obeyUserWorkingProfileAndBankHolidays: true,
    autoAdjustBookingForWorkingProfileAndBankHolidays: false,
    addDaysToEndOfBooking: false,
    allowRepeatBookings: true,
    requiresApproval: false,
    allowOverlapping: false,
    fieldConfigurations: generateDefaultFieldConfigurations(),
    fieldOrder: generateDefaultFieldOrder(),
    color: '#8b5cf6',
    icon: 'Building',
    allowedRoles: ['Administrator', 'User'],
    createdAt: '2024-01-01T00:00:00.000Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00.000Z',
    updatedBy: 'system',
    version: 1,
    isTemporary: false,
  },
  {
    id: 'bt-maintenance-001',
    code: 'MAINTENANCE',
    name: 'System Maintenance',
    description: 'System maintenance, updates, and technical work',
    category: 'maintenance',
    active: true,
    isDefault: false,
    sortOrder: 6,
    autoUpdateTimesheet: true,
    doNotUpdateTimesheetWithHours: false,
    obeyUserWorkingProfileAndBankHolidays: false,
    autoAdjustBookingForWorkingProfileAndBankHolidays: false,
    addDaysToEndOfBooking: false,
    allowRepeatBookings: true,
    requiresApproval: false,
    allowOverlapping: false,
    fieldConfigurations: generateDefaultFieldConfigurations(),
    fieldOrder: generateDefaultFieldOrder(),
    color: '#ef4444',
    icon: 'Wrench',
    allowedRoles: ['Administrator'],
    createdAt: '2024-01-01T00:00:00.000Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00.000Z',
    updatedBy: 'system',
    version: 1,
    isTemporary: false,
  },
  {
    id: 'bt-other-001',
    code: 'OTHER',
    name: 'Other',
    description: 'Miscellaneous bookings that don\'t fit other categories',
    category: 'other',
    active: true,
    isDefault: false,
    sortOrder: 7,
    autoUpdateTimesheet: true,
    doNotUpdateTimesheetWithHours: false,
    obeyUserWorkingProfileAndBankHolidays: true,
    autoAdjustBookingForWorkingProfileAndBankHolidays: false,
    addDaysToEndOfBooking: false,
    allowRepeatBookings: true,
    requiresApproval: false,
    allowOverlapping: false,
    fieldConfigurations: generateDefaultFieldConfigurations(),
    fieldOrder: generateDefaultFieldOrder(),
    color: '#64748b',
    icon: 'MoreHorizontal',
    allowedRoles: ['Administrator', 'User'],
    createdAt: '2024-01-01T00:00:00.000Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00.000Z',
    updatedBy: 'system',
    version: 1,
    isTemporary: false,
  },
];

// ============================================================================
// BOOKINGS SEED DATA
// ============================================================================

/**
 * Helper function to generate dates relative to today
 */
const getRelativeDate = (daysOffset: number, hours: number = 9, minutes: number = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

/**
 * Helper function to calculate duration in hours
 */
const calculateDurationHours = (startTime: string, endTime: string): number => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
};

/**
 * Helper function to calculate duration in days
 */
const calculateDurationDays = (startTime: string, endTime: string): number => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  return hours / 8; // Assuming 8-hour workday
};

/**
 * Seed data for bookings
 * Includes various scenarios and edge cases
 */
export const seedBookings: BookingRecord[] = [
  // Today's bookings
  {
    id: 'booking-001',
    bookingTypeId: 'bt-project-001',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    clientId: 'c1',
    title: 'Frontend Development Sprint',
    description: 'Implement new UI components for the homepage',
    startTime: getRelativeDate(0, 9, 0),
    endTime: getRelativeDate(0, 12, 0),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 3,
    durationDays: 0.375,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    taskDescription: 'Build responsive navigation component',
    bookingNotes: 'Client requested mobile-first approach',
    tags: ['frontend', 'ui', 'sprint-1'],
    notifyReminders: true,
    reminderMinutes: [30, 60],
    requiresApproval: false,
    color: '#3b82f6',
    createdAt: getRelativeDate(-7, 10, 0),
    createdBy: 'user-001',
    updatedAt: getRelativeDate(-7, 10, 0),
    updatedBy: 'user-001',
    version: 1,
    isDraft: false,
    isDeleted: false,
  },
  {
    id: 'booking-002',
    bookingTypeId: 'bt-project-001',
    resourceId: '1',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc1',
    clientId: 'c2',
    title: 'Third-Party API Integration',
    description: 'Connect payment gateway and authentication services',
    startTime: getRelativeDate(0, 13, 0),
    endTime: getRelativeDate(0, 17, 0),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 4,
    durationDays: 0.5,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    taskDescription: 'Implement OAuth2 flow',
    bookingNotes: 'Security review required before deployment',
    internalNotes: 'Client has tight deadline - prioritize this',
    tags: ['backend', 'api', 'security'],
    notifyReminders: true,
    reminderMinutes: [15],
    requiresApproval: false,
    billableHours: 4,
    hourlyRate: 150,
    estimatedCost: 600,
    color: '#3b82f6',
    createdAt: getRelativeDate(-5, 14, 0),
    createdBy: 'user-001',
    updatedAt: getRelativeDate(-5, 14, 0),
    updatedBy: 'user-001',
    version: 1,
    isDraft: false,
    isDeleted: false,
  },
  {
    id: 'booking-003',
    bookingTypeId: 'bt-meeting-001',
    resourceId: '2',
    resourceIds: ['2', '3', '7'],
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc2',
    clientId: 'c1',
    title: 'Design Review Meeting',
    description: 'Review UI mockups and prototypes with stakeholders',
    startTime: getRelativeDate(0, 10, 0),
    endTime: getRelativeDate(0, 11, 30),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 1.5,
    durationDays: 0.1875,
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    bookingNotes: 'Bring printed mockups for discussion',
    tags: ['design', 'meeting', 'stakeholder'],
    notifyReminders: true,
    reminderMinutes: [30],
    requiresApproval: false,
    color: '#f59e0b',
    createdAt: getRelativeDate(-3, 9, 0),
    createdBy: 'user-002',
    updatedAt: getRelativeDate(-3, 9, 0),
    updatedBy: 'user-002',
    version: 1,
    isDraft: false,
    isDeleted: false,
  },
  // Tomorrow's bookings
  {
    id: 'booking-004',
    bookingTypeId: 'bt-training-001',
    resourceId: '4',
    resourceIds: ['4', '5'],
    projectId: 'pd12',
    projectName: 'Internal Training',
    costCentreId: 'cc7',
    title: 'Advanced TypeScript Workshop',
    description: 'Deep dive into TypeScript advanced features and best practices',
    startTime: getRelativeDate(1, 9, 0),
    endTime: getRelativeDate(1, 17, 0),
    timezone: 'America/New_York',
    allDay: true,
    durationType: 'full-day',
    durationHours: 8,
    durationDays: 1,
    billingClassification: 'internal',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'medium',
    taskDescription: 'Complete all workshop exercises',
    bookingNotes: 'Bring laptop with VS Code installed',
    tags: ['training', 'typescript', 'development'],
    notifyReminders: true,
    reminderMinutes: [1440], // 1 day before
    requiresApproval: true,
    approvalStatus: 'approved',
    approvedBy: 'manager-001',
    approvedAt: getRelativeDate(-2, 15, 0),
    color: '#10b981',
    createdAt: getRelativeDate(-10, 11, 0),
    createdBy: 'user-004',
    updatedAt: getRelativeDate(-2, 15, 0),
    updatedBy: 'manager-001',
    version: 2,
    isDraft: false,
    isDeleted: false,
  },
  {
    id: 'booking-005',
    bookingTypeId: 'bt-project-001',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc5',
    clientId: 'c2',
    title: 'Cloud Infrastructure Deployment',
    description: 'Set up production environment on AWS',
    startTime: getRelativeDate(1, 8, 0),
    endTime: getRelativeDate(1, 12, 0),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 4,
    durationDays: 0.5,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    taskDescription: 'Configure load balancers and auto-scaling',
    bookingNotes: 'Coordinate with security team for VPC setup',
    internalNotes: 'High-priority client - ensure no delays',
    tags: ['devops', 'aws', 'infrastructure'],
    notifyReminders: true,
    reminderMinutes: [60],
    requiresApproval: false,
    billableHours: 4,
    hourlyRate: 175,
    estimatedCost: 700,
    color: '#3b82f6',
    createdAt: getRelativeDate(-4, 16, 0),
    createdBy: 'user-006',
    updatedAt: getRelativeDate(-4, 16, 0),
    updatedBy: 'user-006',
    version: 1,
    isDraft: false,
    isDeleted: false,
  },
  // Next week's bookings
  {
    id: 'booking-006',
    bookingTypeId: 'bt-absence-001',
    resourceId: '3',
    projectId: 'pd12',
    projectName: 'Internal',
    title: 'Annual Leave',
    description: 'Vacation time',
    startTime: getRelativeDate(7, 0, 0),
    endTime: getRelativeDate(11, 23, 59),
    timezone: 'America/New_York',
    allDay: true,
    durationType: 'full-day',
    durationHours: 40,
    durationDays: 5,
    billingClassification: 'non-billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'low',
    bookingNotes: 'Out of office - emergency contact via email only',
    tags: ['vacation', 'absence'],
    notifyReminders: false,
    requiresApproval: true,
    approvalStatus: 'approved',
    approvedBy: 'manager-002',
    approvedAt: getRelativeDate(-14, 10, 0),
    color: '#94a3b8',
    createdAt: getRelativeDate(-30, 9, 0),
    createdBy: 'user-003',
    updatedAt: getRelativeDate(-14, 10, 0),
    updatedBy: 'manager-002',
    version: 2,
    isDraft: false,
    isDeleted: false,
  },
  {
    id: 'booking-007',
    bookingTypeId: 'bt-internal-001',
    resourceId: '8',
    projectId: 'pd9',
    projectName: 'Data Analytics Dashboard',
    costCentreId: 'cc6',
    clientId: 'c3',
    title: 'Data Analysis & Reporting',
    description: 'Analyze user behavior data and create visualizations',
    startTime: getRelativeDate(7, 9, 0),
    endTime: getRelativeDate(7, 16, 0),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 7,
    durationDays: 0.875,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    taskDescription: 'Create dashboard with key metrics',
    bookingNotes: 'Waiting for data access permissions',
    tags: ['analytics', 'data', 'reporting'],
    notifyReminders: true,
    reminderMinutes: [120],
    requiresApproval: false,
    billableHours: 7,
    hourlyRate: 140,
    estimatedCost: 980,
    color: '#8b5cf6',
    createdAt: getRelativeDate(-2, 13, 0),
    createdBy: 'user-008',
    updatedAt: getRelativeDate(-2, 13, 0),
    updatedBy: 'user-008',
    version: 1,
    isDraft: false,
    isDeleted: false,
  },
  // Planned booking
  {
    id: 'booking-008',
    bookingTypeId: 'bt-project-001',
    resourceId: '7',
    projectId: 'pd6',
    projectName: 'Component Library',
    costCentreId: 'cc1',
    clientId: 'c4',
    title: 'Build Reusable UI Components',
    description: 'Create component library for design system',
    startTime: getRelativeDate(3, 10, 0),
    endTime: getRelativeDate(3, 15, 0),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 5,
    durationDays: 0.625,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'low',
    taskDescription: 'Button, Input, and Card components',
    bookingNotes: 'Need to confirm with client',
    tags: ['frontend', 'components'],
    notifyReminders: false,
    requiresApproval: false,
    color: '#3b82f6',
    createdAt: getRelativeDate(-1, 17, 0),
    createdBy: 'user-007',
    updatedAt: getRelativeDate(-1, 17, 0),
    updatedBy: 'user-007',
    version: 1,
    isDraft: true,
    isDeleted: false,
  },
  // Recurring booking (parent)
  {
    id: 'booking-009',
    bookingTypeId: 'bt-meeting-001',
    resourceId: '3',
    resourceIds: ['1', '2', '3', '7'],
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    clientId: 'c5',
    title: 'Weekly Team Standup',
    description: 'Weekly team sync and progress updates',
    startTime: getRelativeDate(0, 9, 0),
    endTime: getRelativeDate(0, 9, 30),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 0.5,
    durationDays: 0.0625,
    recurrence: {
      pattern: 'weekly',
      interval: 1,
      endType: 'after',
      occurrences: 12,
    },
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingNotes: 'Recurring every Monday',
    tags: ['meeting', 'standup', 'recurring'],
    notifyReminders: true,
    reminderMinutes: [15],
    requiresApproval: false,
    color: '#f59e0b',
    createdAt: getRelativeDate(-30, 8, 0),
    createdBy: 'user-003',
    updatedAt: getRelativeDate(-30, 8, 0),
    updatedBy: 'user-003',
    version: 1,
    isDraft: false,
    isDeleted: false,
  },
  // Deleted booking (soft delete)
  {
    id: 'booking-010',
    bookingTypeId: 'bt-project-001',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    clientId: 'c3',
    title: 'Regression Testing',
    description: 'Test all features after latest deployment',
    startTime: getRelativeDate(-2, 14, 0),
    endTime: getRelativeDate(-2, 17, 0),
    timezone: 'America/New_York',
    allDay: false,
    durationType: 'hourly',
    durationHours: 3,
    durationDays: 0.375,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'cancelled',
    priority: 'medium',
    taskDescription: 'Run automated test suite',
    bookingNotes: 'Cancelled due to deployment delay',
    tags: ['testing', 'qa', 'cancelled'],
    notifyReminders: false,
    requiresApproval: false,
    color: '#3b82f6',
    createdAt: getRelativeDate(-7, 11, 0),
    createdBy: 'user-005',
    updatedAt: getRelativeDate(-3, 16, 0),
    updatedBy: 'user-005',
    version: 2,
    isDraft: false,
    isDeleted: true,
    deletedAt: getRelativeDate(-3, 16, 0),
    deletedBy: 'user-005',
  },
];

// ============================================================================
// BOOKING HISTORY SEED DATA
// ============================================================================

/**
 * Seed data for booking history
 * Tracks changes to bookings
 */
export const seedBookingHistory: BookingHistoryRecord[] = [
  {
    id: 'history-001',
    bookingId: 'booking-001',
    changeType: 'created',
    changedFields: [],
    newState: {
      title: 'Frontend Development Sprint',
      status: 'confirmed',
    },
    changedAt: getRelativeDate(-7, 10, 0),
    changedBy: 'user-001',
    changedByName: 'Sarah Johnson',
  },
  {
    id: 'history-002',
    bookingId: 'booking-004',
    changeType: 'created',
    changedFields: [],
    newState: {
      title: 'Advanced TypeScript Workshop',
      status: 'planned',
    },
    changedAt: getRelativeDate(-10, 11, 0),
    changedBy: 'user-004',
    changedByName: 'David Kim',
  },
  {
    id: 'history-003',
    bookingId: 'booking-004',
    changeType: 'approved',
    changedFields: [
      {
        fieldName: 'approvalStatus',
        oldValue: null,
        newValue: 'approved',
        dataType: 'string',
      },
      {
        fieldName: 'status',
        oldValue: 'planned',
        newValue: 'confirmed',
        dataType: 'string',
      },
    ],
    previousState: {
      approvalStatus: undefined,
      status: 'planned',
    },
    newState: {
      approvalStatus: 'approved',
      status: 'confirmed',
    },
    changeReason: 'Training approved by manager',
    changedAt: getRelativeDate(-2, 15, 0),
    changedBy: 'manager-001',
    changedByName: 'John Manager',
  },
  {
    id: 'history-004',
    bookingId: 'booking-006',
    changeType: 'created',
    changedFields: [],
    newState: {
      title: 'Annual Leave',
      status: 'planned',
    },
    changedAt: getRelativeDate(-30, 9, 0),
    changedBy: 'user-003',
    changedByName: 'Emily Rodriguez',
  },
  {
    id: 'history-005',
    bookingId: 'booking-006',
    changeType: 'approved',
    changedFields: [
      {
        fieldName: 'approvalStatus',
        oldValue: null,
        newValue: 'approved',
        dataType: 'string',
      },
      {
        fieldName: 'status',
        oldValue: 'planned',
        newValue: 'confirmed',
        dataType: 'string',
      },
    ],
    previousState: {
      approvalStatus: undefined,
      status: 'planned',
    },
    newState: {
      approvalStatus: 'approved',
      status: 'confirmed',
    },
    changeReason: 'Annual leave approved',
    changedAt: getRelativeDate(-14, 10, 0),
    changedBy: 'manager-002',
    changedByName: 'Jane Manager',
  },
  {
    id: 'history-006',
    bookingId: 'booking-010',
    changeType: 'created',
    changedFields: [],
    newState: {
      title: 'Regression Testing',
      status: 'confirmed',
    },
    changedAt: getRelativeDate(-7, 11, 0),
    changedBy: 'user-005',
    changedByName: 'Jessica Williams',
  },
  {
    id: 'history-007',
    bookingId: 'booking-010',
    changeType: 'deleted',
    changedFields: [
      {
        fieldName: 'status',
        oldValue: 'confirmed',
        newValue: 'cancelled',
        dataType: 'string',
      },
      {
        fieldName: 'isDeleted',
        oldValue: false,
        newValue: true,
        dataType: 'boolean',
      },
    ],
    previousState: {
      status: 'confirmed',
      isDeleted: false,
    },
    newState: {
      status: 'cancelled',
      isDeleted: true,
    },
    changeReason: 'Deployment delayed',
    changeNotes: 'Will reschedule once deployment is complete',
    changedAt: getRelativeDate(-3, 16, 0),
    changedBy: 'user-005',
    changedByName: 'Jessica Williams',
  },
];

// ============================================================================
// BOOKING CONFLICTS SEED DATA
// ============================================================================

/**
 * Seed data for booking conflicts
 * Examples of different conflict scenarios
 */
export const seedBookingConflicts: BookingConflictRecord[] = [
  {
    id: 'conflict-001',
    bookingId1: 'booking-001',
    bookingId2: 'booking-002',
    resourceId: '1',
    conflictType: 'time_overlap',
    conflictSeverity: 'warning',
    overlapStartTime: getRelativeDate(0, 12, 0),
    overlapEndTime: getRelativeDate(0, 12, 0),
    overlapDurationMinutes: 0,
    isResolved: true,
    resolutionType: 'rescheduled',
    resolvedAt: getRelativeDate(-5, 14, 30),
    resolvedBy: 'user-001',
    resolutionNotes: 'Adjusted end time of first booking to avoid overlap',
    detectedAt: getRelativeDate(-5, 14, 0),
    detectedBy: 'system',
  },
];

// ============================================================================
// BOOKING TEMPLATES SEED DATA
// ============================================================================

/**
 * Seed data for booking templates
 * Reusable templates for common booking scenarios
 */
export const seedBookingTemplates: BookingTemplateRecord[] = [
  {
    id: 'template-001',
    name: 'Standard Project Work (4 hours)',
    description: 'Template for standard 4-hour project work session',
    category: 'project',
    bookingTypeId: 'bt-project-001',
    templateData: {
      durationType: 'hourly',
      durationHours: 4,
      durationDays: 0.5,
      billingClassification: 'billable',
      workType: 'remote',
      status: 'planned',
      priority: 'medium',
      notifyReminders: true,
      reminderMinutes: [30],
      requiresApproval: false,
      isDraft: false,
      isDeleted: false,
    },
    isPublic: true,
    createdBy: 'system',
    usageCount: 15,
    lastUsedAt: getRelativeDate(-1, 14, 0),
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    id: 'template-002',
    name: 'Full Day Training',
    description: 'Template for full-day training sessions',
    category: 'training',
    bookingTypeId: 'bt-training-001',
    templateData: {
      durationType: 'full-day',
      durationHours: 8,
      durationDays: 1,
      allDay: true,
      billingClassification: 'internal',
      workType: 'onsite',
      status: 'planned',
      priority: 'medium',
      notifyReminders: true,
      reminderMinutes: [1440],// 1 day before
      requiresApproval: true,
      isDraft: false,
      isDeleted: false,
    },
    isPublic: true,
    createdBy: 'system',
    usageCount: 8,
    lastUsedAt: getRelativeDate(-10, 11, 0),
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    id: 'template-003',
    name: 'Team Meeting (30 min)',
    description: 'Template for short team meetings',
    category: 'meeting',
    bookingTypeId: 'bt-meeting-001',
    templateData: {
      durationType: 'hourly',
      durationHours: 0.5,
      durationDays: 0.0625,
      billingClassification: 'internal',
      workType: 'remote',
      status: 'confirmed',
      priority: 'medium',
      notifyReminders: true,
      reminderMinutes: [15],
      requiresApproval: false,
      isDraft: false,
      isDeleted: false,
    },
    isPublic: true,
    createdBy: 'system',
    usageCount: 42,
    lastUsedAt: getRelativeDate(0, 9, 0),
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    id: 'template-004',
    name: 'Half Day Leave (AM)',
    description: 'Template for morning half-day absence',
    category: 'absence',
    bookingTypeId: 'bt-absence-001',
    templateData: {
      durationType: 'half-day-am',
      halfDayPeriod: 'am',
      durationHours: 4,
      durationDays: 0.5,
      billingClassification: 'non-billable',
      workType: 'remote',
      status: 'planned',
      priority: 'low',
      notifyReminders: false,
      requiresApproval: true,
      isDraft: false,
      isDeleted: false,
    },
    isPublic: true,
    createdBy: 'system',
    usageCount: 6,
    lastUsedAt: getRelativeDate(-15, 9, 0),
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    id: 'template-005',
    name: 'Client Meeting (1 hour)',
    description: 'Template for standard client meetings',
    category: 'meeting',
    bookingTypeId: 'bt-meeting-001',
    templateData: {
      durationType: 'hourly',
      durationHours: 1,
      durationDays: 0.125,
      billingClassification: 'billable',
      workType: 'hybrid',
      status: 'confirmed',
      priority: 'high',
      notifyReminders: true,
      reminderMinutes: [30, 60],
      requiresApproval: false,
      isDraft: false,
      isDeleted: false,
    },
    isPublic: true,
    createdBy: 'system',
    usageCount: 23,
    lastUsedAt: getRelativeDate(-3, 10, 0),
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
];

// ============================================================================
// EXPORT ALL SEED DATA
// ============================================================================

export const allSeedData = {
  bookingTypes: seedBookingTypes,
  bookings: seedBookings,
  bookingHistory: seedBookingHistory,
  bookingConflicts: seedBookingConflicts,
  bookingTemplates: seedBookingTemplates,
};

/**
 * Get seed data statistics
 */
export const getSeedDataStats = () => ({
  bookingTypes: seedBookingTypes.length,
  bookings: seedBookings.length,
  bookingHistory: seedBookingHistory.length,
  bookingConflicts: seedBookingConflicts.length,
  bookingTemplates: seedBookingTemplates.length,
  total: 
    seedBookingTypes.length +
    seedBookings.length +
    seedBookingHistory.length +
    seedBookingConflicts.length +
    seedBookingTemplates.length,
});
